/**
 * DatacenterDiagram — Visual datacenter topology.
 *
 * Three views:
 *   floor    — Floor plan: locations as sections, racks as mini-cards
 *   topology — D3 force-directed network graph (elements + cables)
 *   racks    — U-space rack view for each rack
 *
 * Uses only libraries already in package.json (D3, React, Bootstrap).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as d3 from 'd3';
import api from '../services/api';

// ── Type configuration ────────────────────────────────────────────────────────

const TYPE_CFG = {
  server:       { color: '#28a745', dark: '#1a6830', icon: '🖥️',  label: 'Servidor',    r: 14 },
  switch:       { color: '#0d6efd', dark: '#0a50c4', icon: '🔀',  label: 'Switch',      r: 16 },
  panel:        { color: '#6c757d', dark: '#4a525a', icon: '🔌',  label: 'Patch Panel', r: 10 },
  pdu:          { color: '#fd7e14', dark: '#c7630e', icon: '⚡',  label: 'PDU',         r: 12 },
  firewall:     { color: '#dc3545', dark: '#b02a37', icon: '🛡️',  label: 'Firewall',    r: 14 },
  router:       { color: '#6f42c1', dark: '#58359a', icon: '🌐',  label: 'Router',      r: 14 },
  chassis:      { color: '#20c997', dark: '#199d79', icon: '📦',  label: 'Chasis',      r: 16 },
  ups:          { color: '#ffc107', dark: '#cc9a06', icon: '🔋',  label: 'UPS',         r: 12 },
  wall_outlet:  { color: '#adb5bd', dark: '#868e96', icon: '🔲',  label: 'Roseta',      r: 8  },
  default:      { color: '#495057', dark: '#343a40', icon: '📟',  label: 'Equipo',      r: 10 },
};

const CABLE_CFG = {
  cat5e:    { color: '#ffc107', label: 'Cat5e'    },
  cat6:     { color: '#28a745', label: 'Cat6'     },
  cat6a:    { color: '#0d6efd', label: 'Cat6A'    },
  cat7:     { color: '#6f42c1', label: 'Cat7'     },
  cat8:     { color: '#fd7e14', label: 'Cat8'     },
  fiber_sm: { color: '#17a2b8', label: 'Fibra SM' },
  fiber_mm: { color: '#dc3545', label: 'Fibra MM' },
  dac:      { color: '#6c757d', label: 'DAC'      },
  aoc:      { color: '#20c997', label: 'AOC'      },
  default:  { color: '#adb5bd', label: 'Cable'    },
};

const tc = (type) => TYPE_CFG[type] || TYPE_CFG.default;
const cc = (type) => CABLE_CFG[type] || CABLE_CFG.default;

// ── D3 topology builder ───────────────────────────────────────────────────────

function buildD3Topology(data, svgEl, setSelected, filterType, searchTerm) {
  // Clear previous graph
  d3.select(svgEl).selectAll('*').remove();

  const width  = svgEl.parentElement?.clientWidth  || 900;
  const height = svgEl.parentElement?.clientHeight || 600;

  // Filter elements
  let elements = data.elements.filter(e => e.element_type !== 'wall_outlet');
  if (filterType !== 'all') {
    elements = elements.filter(e => e.element_type === filterType);
  }
  if (searchTerm) {
    const q = searchTerm.toLowerCase();
    elements = elements.filter(e =>
      e.name?.toLowerCase().includes(q) ||
      e.code?.toLowerCase().includes(q) ||
      e.ip_address?.toLowerCase().includes(q)
    );
  }

  const elementIds = new Set(elements.map(e => String(e.id)));

  // Filter connections to only involve visible elements
  const links = data.connections.filter(c =>
    elementIds.has(String(c.source_element_id)) &&
    elementIds.has(String(c.target_element_id)) &&
    String(c.source_element_id) !== String(c.target_element_id)
  ).map(c => ({
    ...c,
    source: String(c.source_element_id),
    target: String(c.target_element_id),
  }));

  const nodes = elements.map(e => ({
    id: String(e.id),
    name: e.name || e.code || 'Equipo',
    type: e.element_type || 'default',
    ip: e.ip_address,
    rack_id: e.rack_id,
    status: e.status,
  }));

  // SVG setup
  const svg = d3.select(svgEl)
    .attr('width', width)
    .attr('height', height);

  // Defs: arrowhead
  const defs = svg.append('defs');
  Object.entries(CABLE_CFG).forEach(([key, cfg]) => {
    defs.append('marker')
      .attr('id', `arrow-${key}`)
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 22)
      .attr('refY', 0)
      .attr('markerWidth', 4)
      .attr('markerHeight', 4)
      .attr('orient', 'auto')
      .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .attr('fill', cfg.color)
        .attr('opacity', 0.6);
  });

  // Zoom container
  const g = svg.append('g').attr('class', 'zoom-group');
  svg.call(
    d3.zoom()
      .scaleExtent([0.1, 5])
      .on('zoom', (event) => g.attr('transform', event.transform))
  );

  // Force simulation
  const simulation = d3.forceSimulation(nodes)
    .force('link', d3.forceLink(links)
      .id(d => d.id)
      .distance(d => {
        const sa = tc(nodes.find(n => n.id === (d.source.id || d.source))?.type);
        const ta = tc(nodes.find(n => n.id === (d.target.id || d.target))?.type);
        return (sa.r + ta.r) * 4 + 20;
      })
      .strength(0.4)
    )
    .force('charge', d3.forceManyBody().strength(-120))
    .force('center', d3.forceCenter(width / 2, height / 2))
    .force('collide', d3.forceCollide().radius(d => tc(d.type).r + 8))
    .alphaDecay(0.028);

  // Draw links
  const link = g.append('g')
    .attr('class', 'links')
    .selectAll('line')
    .data(links)
    .join('line')
      .attr('stroke', d => cc(d.cable_type).color)
      .attr('stroke-width', 1.5)
      .attr('stroke-opacity', 0.55)
      .attr('marker-end', d => `url(#arrow-${d.cable_type || 'default'})`);

  // Link hover tooltip
  link.append('title')
    .text(d => `${cc(d.cable_type).label}${d.cable_length_m ? ` · ${d.cable_length_m}m` : ''}${d.notes ? '\n' + d.notes : ''}`);

  // Draw node groups
  const node = g.append('g')
    .attr('class', 'nodes')
    .selectAll('g')
    .data(nodes)
    .join('g')
      .attr('class', 'node-group')
      .style('cursor', 'pointer')
      .call(
        d3.drag()
          .on('start', (event, d) => {
            if (!event.active) simulation.alphaTarget(0.3).restart();
            d.fx = d.x; d.fy = d.y;
          })
          .on('drag', (event, d) => { d.fx = event.x; d.fy = event.y; })
          .on('end', (event, d) => {
            if (!event.active) simulation.alphaTarget(0);
            d.fx = null; d.fy = null;
          })
      )
      .on('click', (event, d) => {
        event.stopPropagation();
        setSelected(d);
      });

  // Node circle
  node.append('circle')
    .attr('r', d => tc(d.type).r)
    .attr('fill', d => tc(d.type).color)
    .attr('stroke', '#fff')
    .attr('stroke-width', 2)
    .attr('filter', d => d.status === 'offline' ? 'url(#offline)' : null);

  // Status ring for offline/maintenance
  node.filter(d => d.status !== 'active')
    .append('circle')
      .attr('r', d => tc(d.type).r + 3)
      .attr('fill', 'none')
      .attr('stroke', d => d.status === 'offline' ? '#dc3545' : '#ffc107')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4 2');

  // Node label
  node.append('text')
    .attr('dy', d => tc(d.type).r + 11)
    .attr('text-anchor', 'middle')
    .attr('font-size', '9px')
    .attr('fill', '#dee2e6')
    .attr('pointer-events', 'none')
    .text(d => d.name.length > 18 ? d.name.substring(0, 17) + '…' : d.name);

  // IP label (smaller, below name)
  node.filter(d => !!d.ip)
    .append('text')
      .attr('dy', d => tc(d.type).r + 21)
      .attr('text-anchor', 'middle')
      .attr('font-size', '8px')
      .attr('fill', '#6c757d')
      .attr('pointer-events', 'none')
      .text(d => d.ip);

  // Tooltip
  node.append('title')
    .text(d => `${d.name}${d.ip ? '\n' + d.ip : ''}\nTipo: ${tc(d.type).label}\nEstado: ${d.status || 'active'}`);

  // Click on background deselects
  svg.on('click', () => setSelected(null));

  // Tick
  simulation.on('tick', () => {
    link
      .attr('x1', d => d.source.x)
      .attr('y1', d => d.source.y)
      .attr('x2', d => d.target.x)
      .attr('y2', d => d.target.y);

    node.attr('transform', d => `translate(${d.x},${d.y})`);
  });

  return simulation;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FloorPlan({ data, navigate }) {
  const { locations, racks, elements } = data;

  // Build a quick lookup: rack_id → elements[]
  const rackElements = {};
  elements.forEach(e => {
    if (e.rack_id) {
      if (!rackElements[e.rack_id]) rackElements[e.rack_id] = [];
      rackElements[e.rack_id].push(e);
    }
  });

  // Build location hierarchy (only top-level or all flat)
  const locationMap = Object.fromEntries(locations.map(l => [l.id, l]));
  const topLevels = locations.filter(l => !l.parent_id);
  const rack_map = {};
  racks.forEach(r => {
    const key = r.location_id || 'unassigned';
    if (!rack_map[key]) rack_map[key] = [];
    rack_map[key].push(r);
  });

  // Count element types per rack
  function typeBreakdown(rackId) {
    const els = rackElements[rackId] || [];
    const counts = {};
    els.forEach(e => {
      const t = e.element_type || 'default';
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }

  const renderRack = (rack) => {
    const breakdown = typeBreakdown(rack.id);
    const total = Object.values(breakdown).reduce((a, b) => a + b, 0);
    return (
      <div
        key={rack.id}
        className="dc-rack-card"
        onClick={() => navigate(`/infrastructure/rack/${rack.id}`)}
        title={`${rack.name}\n${rack.code}\n${total} elemento(s)`}
      >
        <div className="dc-rack-name">{rack.code || rack.name}</div>
        <div className="dc-rack-dots">
          {Object.entries(breakdown).map(([type, count]) => (
            <span
              key={type}
              className="dc-rack-dot"
              style={{ backgroundColor: tc(type).color }}
              title={`${tc(type).label}: ${count}`}
            >
              {count}
            </span>
          ))}
          {total === 0 && <span className="dc-rack-dot" style={{ backgroundColor: '#343a40' }}>0</span>}
        </div>
        <div className="dc-rack-total">{total}U occ.</div>
      </div>
    );
  };

  const renderLocation = (loc) => {
    const locRacks = rack_map[loc.id] || [];
    const children = locations.filter(l => l.parent_id === loc.id);
    return (
      <div key={loc.id} className="dc-location-card">
        <div className="dc-location-header">
          <span className="dc-location-icon">
            {loc.location_type === 'building' ? '🏢' : loc.location_type === 'room' ? '🏛️' : '🔲'}
          </span>
          <span className="dc-location-name">{loc.name}</span>
          {loc.floor && <span className="dc-location-floor">Planta {loc.floor}</span>}
          <span className="dc-location-count">{locRacks.length} racks</span>
        </div>
        <div className="dc-rack-grid">
          {locRacks.map(renderRack)}
          {children.map(child => (
            <div key={child.id} className="dc-child-location">
              <div className="dc-location-header dc-location-header--child">
                <span className="dc-location-icon">🔲</span>
                <span className="dc-location-name">{child.name}</span>
                <span className="dc-location-count">{(rack_map[child.id] || []).length} racks</span>
              </div>
              <div className="dc-rack-grid">
                {(rack_map[child.id] || []).map(renderRack)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const unassignedRacks = rack_map['unassigned'] || [];

  return (
    <div className="dc-floor-plan">
      {topLevels.map(renderLocation)}
      {unassignedRacks.length > 0 && (
        <div className="dc-location-card">
          <div className="dc-location-header">
            <span className="dc-location-icon">📦</span>
            <span className="dc-location-name">Sin ubicación</span>
            <span className="dc-location-count">{unassignedRacks.length} racks</span>
          </div>
          <div className="dc-rack-grid">{unassignedRacks.map(renderRack)}</div>
        </div>
      )}
      {topLevels.length === 0 && unassignedRacks.length === 0 && racks.length === 0 && (
        <div className="dc-empty">
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏢</div>
          <p>No hay racks configurados. Empieza creando ubicaciones y racks.</p>
        </div>
      )}
    </div>
  );
}

function RackMatrix({ data, navigate }) {
  const { racks, elements } = data;

  const rackElements = {};
  elements.forEach(e => {
    if (e.rack_id) {
      if (!rackElements[e.rack_id]) rackElements[e.rack_id] = [];
      rackElements[e.rack_id].push(e);
    }
  });

  const [selectedEl, setSelectedEl] = useState(null);

  const renderRack = (rack) => {
    const els = rackElements[rack.id] || [];
    const heightU = rack.height_u || 42;
    // Build U map: position_u → element
    const uMap = {};
    els.forEach(e => {
      if (e.position_u) {
        for (let i = 0; i < (e.size_u || 1); i++) {
          uMap[e.position_u + i] = e;
        }
      }
    });

    return (
      <div key={rack.id} className="dc-rack-matrix">
        <div className="dc-rack-matrix-header" onClick={() => navigate(`/infrastructure/rack/${rack.id}`)}>
          <span>{rack.code}</span>
          <small>{rack.name}</small>
        </div>
        <div className="dc-rack-matrix-body">
          {Array.from({ length: heightU }, (_, i) => {
            const u = i + 1;
            const el = uMap[u];
            const conf = el ? tc(el.element_type) : null;
            // Skip if this U is occupied by a multi-U element that started earlier
            if (el && el.position_u && el.position_u < u) return null;
            return (
              <div
                key={u}
                className={`dc-rack-u ${el ? 'occupied' : 'empty'}`}
                style={{
                  height: `${Math.max(1, el?.size_u || 1) * 18}px`,
                  backgroundColor: conf ? conf.color + '44' : undefined,
                  borderLeft: conf ? `3px solid ${conf.color}` : undefined,
                }}
                onClick={() => el && setSelectedEl(el)}
                title={el ? `${el.name} (${el.element_type}) U${el.position_u}` : `U${u} libre`}
              >
                <span className="dc-rack-u-num">{u}</span>
                {el && (
                  <span className="dc-rack-u-name">
                    <span style={{ marginRight: 4 }}>{conf.icon}</span>
                    {el.name}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="dc-rack-matrix-wrap">
      <div className="dc-rack-matrix-grid">
        {racks.map(renderRack)}
        {racks.length === 0 && (
          <div className="dc-empty">
            <div style={{ fontSize: '3rem' }}>🗄️</div>
            <p>No hay racks configurados.</p>
          </div>
        )}
      </div>

      {selectedEl && (
        <div className="dc-detail-panel">
          <div className="dc-detail-header">
            <span style={{ fontSize: '1.5rem' }}>{tc(selectedEl.element_type).icon}</span>
            <div>
              <strong>{selectedEl.name}</strong>
              <small>{selectedEl.code}</small>
            </div>
            <button className="btn-close btn-close-white" onClick={() => setSelectedEl(null)} />
          </div>
          <div className="dc-detail-body">
            <div className="dc-detail-row"><span>Tipo</span><span>{tc(selectedEl.element_type).label}</span></div>
            {selectedEl.ip_address && <div className="dc-detail-row"><span>IP</span><span>{selectedEl.ip_address}</span></div>}
            {selectedEl.manufacturer && <div className="dc-detail-row"><span>Fabricante</span><span>{selectedEl.manufacturer}</span></div>}
            {selectedEl.model && <div className="dc-detail-row"><span>Modelo</span><span>{selectedEl.model}</span></div>}
            <div className="dc-detail-row"><span>Estado</span>
              <span className={`badge bg-${selectedEl.status === 'active' ? 'success' : selectedEl.status === 'maintenance' ? 'warning' : 'secondary'}`}>
                {selectedEl.status || 'activo'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Legend component ──────────────────────────────────────────────────────────

function Legend({ showCables }) {
  return (
    <div className="dc-legend">
      <div className="dc-legend-section">
        <span className="dc-legend-title">Tipos</span>
        {Object.entries(TYPE_CFG).filter(([k]) => k !== 'default').map(([key, cfg]) => (
          <span key={key} className="dc-legend-item">
            <span className="dc-legend-dot" style={{ backgroundColor: cfg.color }} />
            {cfg.label}
          </span>
        ))}
      </div>
      {showCables && (
        <div className="dc-legend-section">
          <span className="dc-legend-title">Cables</span>
          {Object.entries(CABLE_CFG).filter(([k]) => k !== 'default').map(([key, cfg]) => (
            <span key={key} className="dc-legend-item">
              <span className="dc-legend-line" style={{ backgroundColor: cfg.color }} />
              {cfg.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const DatacenterDiagram = ({ companyId }) => {
  const navigate = useNavigate();
  const svgRef = useRef(null);
  const simRef = useRef(null);

  const [viewMode, setViewMode] = useState(() =>
    localStorage.getItem('dc_diagram_view') || 'floor'
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    localStorage.setItem('dc_diagram_view', viewMode);
  }, [viewMode]);

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getDatacenterTopology(companyId);
      setData(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Build / rebuild D3 graph whenever topology view is active
  useEffect(() => {
    if (viewMode !== 'topology' || !data || !svgRef.current) return;
    if (simRef.current) { simRef.current.stop(); simRef.current = null; }
    const sim = buildD3Topology(data, svgRef.current, setSelectedNode, filterType, searchTerm);
    simRef.current = sim;
    return () => { if (simRef.current) simRef.current.stop(); };
  }, [viewMode, data, filterType, searchTerm]);

  // ── Unique types for filter dropdown ──
  const availableTypes = data
    ? [...new Set(data.elements.map(e => e.element_type).filter(Boolean))]
    : [];

  // ── Selected node detail (topology view) ──
  const nodeConnections = selectedNode && data
    ? data.connections.filter(c =>
        String(c.source_element_id) === selectedNode.id ||
        String(c.target_element_id) === selectedNode.id
      )
    : [];

  const connectedEls = nodeConnections.map(c => {
    const otherId = String(c.source_element_id) === selectedNode?.id
      ? String(c.target_element_id)
      : String(c.source_element_id);
    return data?.elements.find(e => String(e.id) === otherId);
  }).filter(Boolean);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="dc-diagram-root">
      {/* ── Header ── */}
      <div className="dc-header">
        <div className="dc-header-left">
          <h5 className="dc-title">🗺️ Diagrama Datacenter</h5>
          {data && (
            <div className="dc-stats">
              <span className="dc-stat">🏢 {data.stats.locations} ubicaciones</span>
              <span className="dc-stat">🗄️ {data.stats.racks} racks</span>
              <span className="dc-stat">📟 {data.stats.elements} elementos</span>
              <span className="dc-stat">🔗 {data.stats.connections} conexiones</span>
            </div>
          )}
        </div>
        <div className="dc-header-right">
          <div className="dc-view-tabs">
            <button
              className={`dc-tab ${viewMode === 'floor' ? 'active' : ''}`}
              onClick={() => setViewMode('floor')}
              title="Vista plano de sala"
            >🏢 Plano</button>
            <button
              className={`dc-tab ${viewMode === 'topology' ? 'active' : ''}`}
              onClick={() => setViewMode('topology')}
              title="Topología de red"
            >🔗 Topología</button>
            <button
              className={`dc-tab ${viewMode === 'racks' ? 'active' : ''}`}
              onClick={() => setViewMode('racks')}
              title="Vista de racks"
            >🗄️ Racks</button>
          </div>
          <button className="dc-refresh" onClick={loadData} title="Recargar datos">
            🔄
          </button>
        </div>
      </div>

      {/* ── Toolbar (topology view only) ── */}
      {viewMode === 'topology' && (
        <div className="dc-toolbar">
          <div className="dc-toolbar-left">
            <input
              type="text"
              className="dc-search"
              placeholder="Buscar elemento…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <select
              className="dc-filter"
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
            >
              <option value="all">Todos los tipos</option>
              {availableTypes.map(t => (
                <option key={t} value={t}>{tc(t).label}</option>
              ))}
            </select>
          </div>
          <div className="dc-toolbar-right">
            <small style={{ color: '#6c757d' }}>
              Arrastra nodos · Rueda para zoom · Click para detalles
            </small>
          </div>
        </div>
      )}

      {/* ── Content area ── */}
      <div className="dc-content">
        {loading && (
          <div className="dc-loading">
            <div className="spinner-border text-primary" role="status" />
            <span>Cargando topología…</span>
          </div>
        )}

        {error && !loading && (
          <div className="dc-error">
            <div style={{ fontSize: '2rem' }}>⚠️</div>
            <p>{error}</p>
            <button className="btn btn-sm btn-outline-primary" onClick={loadData}>Reintentar</button>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Floor plan */}
            {viewMode === 'floor' && (
              <>
                <FloorPlan data={data} navigate={navigate} />
                <Legend showCables={false} />
              </>
            )}

            {/* Topology D3 */}
            {viewMode === 'topology' && (
              <div className="dc-topology-wrap">
                <svg
                  ref={svgRef}
                  className="dc-topology-svg"
                  style={{ background: '#0d1117', borderRadius: 8 }}
                />
                <Legend showCables />

                {/* Selected node detail panel */}
                {selectedNode && (
                  <div className="dc-node-detail">
                    <div className="dc-detail-header">
                      <span style={{ fontSize: '1.8rem' }}>{tc(selectedNode.type).icon}</span>
                      <div>
                        <strong>{selectedNode.name}</strong>
                        {selectedNode.ip && <small>{selectedNode.ip}</small>}
                      </div>
                      <button
                        className="btn-close btn-close-white"
                        style={{ marginLeft: 'auto' }}
                        onClick={() => setSelectedNode(null)}
                      />
                    </div>
                    <div className="dc-detail-body">
                      <div className="dc-detail-row">
                        <span>Tipo</span>
                        <span>
                          <span
                            className="badge"
                            style={{ backgroundColor: tc(selectedNode.type).color }}
                          >
                            {tc(selectedNode.type).label}
                          </span>
                        </span>
                      </div>
                      <div className="dc-detail-row">
                        <span>Estado</span>
                        <span className={`badge bg-${selectedNode.status === 'active' ? 'success' : selectedNode.status === 'maintenance' ? 'warning' : 'secondary'}`}>
                          {selectedNode.status || 'activo'}
                        </span>
                      </div>
                      {connectedEls.length > 0 && (
                        <div className="dc-detail-connections">
                          <span className="dc-detail-connections-title">
                            Conexiones ({connectedEls.length})
                          </span>
                          {connectedEls.map((el, i) => (
                            <div key={i} className="dc-detail-conn-row">
                              <span>{tc(el.element_type).icon}</span>
                              <span>{el.name}</span>
                              <span style={{ color: cc(nodeConnections[i]?.cable_type).color, fontSize: '0.75rem' }}>
                                {cc(nodeConnections[i]?.cable_type).label}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Rack matrix */}
            {viewMode === 'racks' && (
              <>
                <RackMatrix data={data} navigate={navigate} />
                <Legend showCables={false} />
              </>
            )}
          </>
        )}

        {!loading && !error && !companyId && (
          <div className="dc-empty">
            <div style={{ fontSize: '3rem' }}>🏢</div>
            <p>Selecciona una empresa para ver su topología de datacenter.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Styles (scoped) ───────────────────────────────────────────────────────────
// Injected via a style tag so we don't pollute global CSS
const styles = `
.dc-diagram-root {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px);
  background: #0d1117;
  color: #e6edf3;
  overflow: hidden;
}

/* Header */
.dc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 10px 16px 8px;
  background: #161b22;
  border-bottom: 1px solid #30363d;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 8px;
}
.dc-header-left { display: flex; flex-direction: column; gap: 4px; }
.dc-title { margin: 0; font-size: 1rem; font-weight: 600; color: #e6edf3; }
.dc-stats { display: flex; flex-wrap: wrap; gap: 12px; }
.dc-stat { font-size: 0.78rem; color: #8b949e; }
.dc-header-right { display: flex; align-items: center; gap: 10px; }

/* View tabs */
.dc-view-tabs { display: flex; background: #0d1117; border: 1px solid #30363d; border-radius: 6px; overflow: hidden; }
.dc-tab {
  padding: 5px 14px; font-size: 0.82rem; background: transparent;
  border: none; color: #8b949e; cursor: pointer; transition: all .15s;
}
.dc-tab:hover { background: #161b22; color: #e6edf3; }
.dc-tab.active { background: #1f6feb; color: #fff; font-weight: 600; }
.dc-refresh {
  background: none; border: 1px solid #30363d; border-radius: 6px;
  color: #8b949e; cursor: pointer; padding: 5px 8px; font-size: 0.9rem;
}
.dc-refresh:hover { border-color: #58a6ff; color: #58a6ff; }

/* Toolbar */
.dc-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 16px; background: #161b22; border-bottom: 1px solid #30363d;
  flex-shrink: 0; flex-wrap: wrap; gap: 8px;
}
.dc-toolbar-left { display: flex; gap: 8px; }
.dc-search {
  background: #0d1117; border: 1px solid #30363d; border-radius: 6px;
  color: #e6edf3; padding: 4px 10px; font-size: 0.82rem; width: 200px;
}
.dc-search:focus { outline: none; border-color: #58a6ff; }
.dc-filter {
  background: #0d1117; border: 1px solid #30363d; border-radius: 6px;
  color: #e6edf3; padding: 4px 8px; font-size: 0.82rem;
}

/* Content */
.dc-content {
  flex: 1; overflow: auto; position: relative;
}
.dc-loading, .dc-error, .dc-empty {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; height: 100%; gap: 12px; color: #8b949e;
}

/* ── Floor plan ── */
.dc-floor-plan {
  padding: 16px; display: flex; flex-wrap: wrap; gap: 16px; align-items: flex-start;
}
.dc-location-card {
  background: #161b22; border: 1px solid #30363d; border-radius: 10px;
  padding: 12px; min-width: 260px; flex: 1 1 320px;
}
.dc-location-header {
  display: flex; align-items: center; gap: 8px; margin-bottom: 10px;
  padding-bottom: 8px; border-bottom: 1px solid #21262d;
}
.dc-location-icon { font-size: 1.1rem; }
.dc-location-name { font-weight: 600; color: #e6edf3; flex: 1; font-size: 0.9rem; }
.dc-location-floor { color: #8b949e; font-size: 0.75rem; }
.dc-location-count { color: #58a6ff; font-size: 0.75rem; font-weight: 600; }
.dc-location-header--child { padding-top: 10px; }

.dc-rack-grid {
  display: flex; flex-wrap: wrap; gap: 8px;
}
.dc-rack-card {
  background: #0d1117; border: 1px solid #21262d; border-radius: 6px;
  padding: 8px 10px; cursor: pointer; transition: all .15s; min-width: 80px;
}
.dc-rack-card:hover { border-color: #58a6ff; background: #161b22; transform: translateY(-1px); }
.dc-rack-name { font-size: 0.72rem; font-weight: 600; color: #e6edf3; margin-bottom: 4px; }
.dc-rack-dots { display: flex; flex-wrap: wrap; gap: 3px; margin-bottom: 3px; }
.dc-rack-dot {
  border-radius: 3px; padding: 1px 4px; font-size: 0.64rem;
  color: #fff; font-weight: 600; min-width: 18px; text-align: center;
}
.dc-rack-total { font-size: 0.65rem; color: #6c757d; }
.dc-child-location { width: 100%; }

/* ── Topology ── */
.dc-topology-wrap {
  position: relative; height: 100%; display: flex; flex-direction: column;
}
.dc-topology-svg { width: 100%; height: 100%; min-height: 500px; }

/* Node detail panel */
.dc-node-detail {
  position: absolute; top: 10px; right: 10px; width: 260px;
  background: #161b22; border: 1px solid #30363d; border-radius: 10px;
  overflow: hidden; box-shadow: 0 8px 24px rgba(0,0,0,.5);
  z-index: 10;
}
.dc-detail-header {
  display: flex; align-items: center; gap: 10px; padding: 10px 12px;
  background: #21262d; border-bottom: 1px solid #30363d;
}
.dc-detail-header > div { display: flex; flex-direction: column; gap: 2px; }
.dc-detail-header strong { font-size: 0.88rem; color: #e6edf3; }
.dc-detail-header small { font-size: 0.75rem; color: #8b949e; }
.dc-detail-body { padding: 10px 12px; }
.dc-detail-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 4px 0; font-size: 0.82rem; border-bottom: 1px solid #21262d;
}
.dc-detail-row > span:first-child { color: #8b949e; }
.dc-detail-connections { margin-top: 8px; }
.dc-detail-connections-title {
  display: block; font-size: 0.75rem; color: #8b949e; margin-bottom: 6px;
  font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;
}
.dc-detail-conn-row {
  display: flex; align-items: center; gap: 6px; padding: 3px 0;
  font-size: 0.8rem; color: #e6edf3;
}

/* ── Rack matrix ── */
.dc-rack-matrix-wrap {
  display: flex; height: 100%; overflow: hidden; position: relative;
}
.dc-rack-matrix-grid {
  display: flex; gap: 12px; padding: 16px; overflow-x: auto; align-items: flex-start; flex: 1;
}
.dc-rack-matrix {
  background: #161b22; border: 1px solid #30363d; border-radius: 8px;
  min-width: 160px; flex-shrink: 0; overflow: hidden;
}
.dc-rack-matrix-header {
  background: #21262d; padding: 8px 10px; cursor: pointer;
  display: flex; flex-direction: column; gap: 2px;
  border-bottom: 1px solid #30363d;
}
.dc-rack-matrix-header span { font-size: 0.82rem; font-weight: 700; color: #58a6ff; }
.dc-rack-matrix-header small { font-size: 0.72rem; color: #8b949e; }
.dc-rack-matrix-header:hover { background: #30363d; }
.dc-rack-matrix-body { overflow-y: auto; max-height: calc(100vh - 180px); }
.dc-rack-u {
  display: flex; align-items: center; gap: 6px; padding: 0 6px;
  border-bottom: 1px solid #0d1117; min-height: 18px; cursor: pointer;
  transition: background .1s;
}
.dc-rack-u.empty { opacity: 0.3; }
.dc-rack-u.occupied:hover { filter: brightness(1.3); }
.dc-rack-u-num {
  font-size: 0.6rem; color: #484f58; min-width: 18px; text-align: right;
}
.dc-rack-u-name {
  font-size: 0.72rem; color: #e6edf3; white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis; max-width: 110px;
}

/* ── Legend ── */
.dc-legend {
  position: absolute; bottom: 12px; left: 12px;
  background: rgba(22,27,34,.9); border: 1px solid #30363d;
  border-radius: 8px; padding: 8px 12px;
  display: flex; gap: 16px; flex-wrap: wrap;
  backdrop-filter: blur(4px); z-index: 5;
}
.dc-legend-section { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.dc-legend-title {
  font-size: 0.7rem; font-weight: 700; color: #8b949e;
  text-transform: uppercase; letter-spacing: 0.06em; margin-right: 4px;
}
.dc-legend-item {
  display: flex; align-items: center; gap: 4px;
  font-size: 0.72rem; color: #8b949e;
}
.dc-legend-dot {
  width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
}
.dc-legend-line {
  width: 16px; height: 3px; border-radius: 2px; flex-shrink: 0;
}

/* D3 node labels */
.dc-topology-svg text { font-family: system-ui, sans-serif; }
`;

// Inject scoped styles once
function InjectStyles() {
  useEffect(() => {
    const id = 'dc-diagram-styles';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id;
      el.textContent = styles;
      document.head.appendChild(el);
    }
    return () => {
      // keep styles mounted — they're cheap to leave
    };
  }, []);
  return null;
}

// Wrapper that injects styles then renders the diagram
const DatacenterDiagramWithStyles = (props) => (
  <>
    <InjectStyles />
    <DatacenterDiagram {...props} />
  </>
);

export default DatacenterDiagramWithStyles;
